import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface Stat {
  id: string;
  value?: string;
  label?: string;
  trend?: string;
}

interface StatsProps {
  stats: Stat[];
}

/**
 * Stats
 *
 * Displays key metrics in a responsive grid with:
 * - Animated number reveal on scroll
 * - Subtle hover effects
 * - Text from translations keyed by stat id
 */
export function Stats({ stats }: StatsProps) {
  const { t } = useTranslation("landing");
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 lg:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Divider line */}
        <hr className="mb-16 border-border lg:mb-24" />

        <div className="grid grid-cols-2 gap-8 lg:grid-cols-4 lg:gap-12">
          {stats.map((stat, index) => (
            <div
              key={stat.id}
              className={cn(
                "group text-center",
                "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
              )}
              style={{
                transitionDelay: isVisible ? `${index * 100}ms` : "0ms",
              }}
            >
              {/* Value */}
              <div className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                {t(`stats.${stat.id}.value`, { defaultValue: stat.value })}
              </div>

              {/* Label */}
              <div className="mt-2 text-sm text-muted-foreground sm:text-base">
                {t(`stats.${stat.id}.label`, { defaultValue: stat.label })}
              </div>

              {/* Trend indicator */}
              <div
                className={cn(
                  "mt-2 inline-flex items-center gap-1",
                  "rounded-full px-2 py-1",
                  "bg-emerald-500/10 text-emerald-400",
                  "text-xs font-medium",
                )}
              >
                {t(`stats.${stat.id}.trend`, { defaultValue: stat.trend })}
              </div>
            </div>
          ))}
        </div>

        {/* Divider line */}
        <hr className="mt-16 border-border lg:mt-24" />
      </div>
    </section>
  );
}
